/**
 * Agent 项目管理 API 服务
 * 调用 axiom-agent-admin 服务
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import { getStoredRegionCode } from '@/shared/services/auth'
import type {
  SuccessResponse,
} from '@/shared/types/api'
import type {
  AgentProject,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectListParams,
  ProjectMember,
  AddMemberRequest,
  UpdateMemberPermissionRequest,
  DuplicateProjectRequest,
  TransferProjectRequest,
  ApiKeyUsageStats,
  ProjectSettings,
  ProjectExportData,
  ImportProjectRequest,
  ImportProjectResult,
} from '@/modules/agent/types/project'

/**
 * Agent 项目管理 API 服务
 */
export class ProjectApiService {
  /**
   * 获取项目列表
   * 注意：项目列表接口不使用分页响应格式，返回用户的所有项目（owned + member）
   */
  static async getProjects(
    params?: ProjectListParams
  ): Promise<SuccessResponse<AgentProject[]>> {
    // 使用 Agent Admin 客户端
    // 注意：路径是 /projects，不是 /agent/admin/projects（baseURL 已包含 /api/v1）
    return ApiClient.getWithClient<AgentProject[]>(
      apiClients.agentAdmin,
      '/projects',
      { params }
    )
  }

  /**
   * 获取项目详情
   */
  static async getProject(projectId: string): Promise<SuccessResponse<AgentProject>> {
    return ApiClient.getWithClient<AgentProject>(
      apiClients.agentAdmin,
      `/projects/${projectId}`
    )
  }

  /**
   * 创建项目
   */
  static async createProject(
    data: CreateProjectRequest
  ): Promise<SuccessResponse<AgentProject>> {
    return ApiClient.postWithClient<AgentProject>(
      apiClients.agentAdmin,
      '/projects',
      data
    )
  }

  /**
   * 更新项目
   */
  static async updateProject(
    projectId: string,
    data: UpdateProjectRequest
  ): Promise<SuccessResponse<AgentProject>> {
    return ApiClient.patchWithClient<AgentProject>(
      apiClients.agentAdmin,
      `/projects/${projectId}`,
      data
    )
  }

  /**
   * 删除项目
   */
  static async deleteProject(
    projectId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}`
    )
  }

  /**
   * 获取项目成员列表
   */
  static async getProjectMembers(
    projectId: string
  ): Promise<SuccessResponse<ProjectMember[]>> {
    return ApiClient.getWithClient<ProjectMember[]>(
      apiClients.agentAdmin,
      `/projects/${projectId}/members`
    )
  }

  /**
   * 添加项目成员
   */
  static async addProjectMember(
    projectId: string,
    data: Omit<AddMemberRequest, 'region_code'>
  ): Promise<SuccessResponse<ProjectMember>> {
    return ApiClient.postWithClient<ProjectMember>(
      apiClients.agentAdmin,
      `/projects/${projectId}/members`,
      { ...data, region_code: getStoredRegionCode() }
    )
  }

  /**
   * 更新成员权限
   */
  static async updateMemberPermission(
    projectId: string,
    userId: string,
    data: UpdateMemberPermissionRequest
  ): Promise<SuccessResponse<ProjectMember>> {
    return ApiClient.patchWithClient<ProjectMember>(
      apiClients.agentAdmin,
      `/projects/${projectId}/members/${userId}`,
      data
    )
  }

  /**
   * 删除项目成员
   */
  static async deleteProjectMember(
    projectId: string,
    userId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/members/${userId}`
    )
  }

  /**
   * 创建 API Key
   * 注意：API Key 只在创建时返回一次，请妥善保存
   */
  static async createApiKey(
    projectId: string
  ): Promise<SuccessResponse<{ id: string; api_key: string; created_at: string }>> {
    return ApiClient.postWithClient<{ id: string; api_key: string; created_at: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/api-keys`
    )
  }

  /**
   * 获取 API Key
   * 注意：不返回完整 key，仅返回 ID 和创建时间
   */
  static async getApiKey(
    projectId: string
  ): Promise<SuccessResponse<{ id: string; api_key: string; created_at: string }>> {
    return ApiClient.getWithClient<{ id: string; api_key: string; created_at: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/api-keys`
    )
  }

  /**
   * 删除 API Key
   */
  static async deleteApiKey(
    projectId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/api-keys`
    )
  }

  /**
   * 重新生成 API Key（兼容旧接口，实际调用创建 API Key）
   * @deprecated 使用 createApiKey 代替
   */
  static async regenerateApiKey(
    projectId: string
  ): Promise<SuccessResponse<{ api_key: string }>> {
    const response = await this.createApiKey(projectId)
    return {
      success: true,
      data: { api_key: response.data.api_key },
      meta: response.meta,
    }
  }

  /**
   * 获取 API Key 使用统计
   * 注意：根据API文档，此接口可能不存在，需要确认后端是否实现
   */
  static async getApiKeyUsageStats(
    projectId: string
  ): Promise<SuccessResponse<ApiKeyUsageStats>> {
    // TODO: 确认API路径，可能需要使用不同的路径或接口
    // 当前API文档中没有此接口的定义，可能需要后端补充
    return ApiClient.getWithClient<ApiKeyUsageStats>(
      apiClients.agentAdmin,
      `/projects/${projectId}/api-keys/stats`
    )
  }

  /**
   * 复制项目
   */
  static async duplicateProject(
    projectId: string,
    data: DuplicateProjectRequest
  ): Promise<SuccessResponse<AgentProject>> {
    return ApiClient.postWithClient<AgentProject>(
      apiClients.agentAdmin,
      `/projects/${projectId}/duplicate`,
      data
    )
  }

  /**
   * 转移项目所有权
   */
  static async transferProject(
    projectId: string,
    data: TransferProjectRequest
  ): Promise<SuccessResponse<AgentProject>> {
    return ApiClient.postWithClient<AgentProject>(
      apiClients.agentAdmin,
      `/projects/${projectId}/transfer`,
      data
    )
  }

  /**
   * 获取项目设置
   * v3.0 新增：用于获取项目级别的配置（如 memory_enabled）
   */
  static async getProjectSettings(
    projectId: string
  ): Promise<SuccessResponse<ProjectSettings>> {
    return ApiClient.getWithClient<ProjectSettings>(
      apiClients.agentAdmin,
      `/projects/${projectId}/settings`
    )
  }

  /**
   * 更新项目设置
   * v3.0 新增：用于更新项目级别的配置（如 memory_enabled）
   */
  static async updateProjectSettings(
    projectId: string,
    data: Partial<ProjectSettings>
  ): Promise<SuccessResponse<ProjectSettings>> {
    return ApiClient.patchWithClient<ProjectSettings>(
      apiClients.agentAdmin,
      `/projects/${projectId}/settings`,
      data
    )
  }

  /**
   * 导出项目配置
   * v5.3 新增：用于跨区域项目迁移
   * 注意：导出不包含 tools、model、user_context_api、enabled_tool_ids、成员
   */
  static async exportProject(
    projectId: string
  ): Promise<SuccessResponse<ProjectExportData>> {
    return ApiClient.getWithClient<ProjectExportData>(
      apiClients.agentAdmin,
      `/projects/${projectId}/export`
    )
  }

  /**
   * 导入项目配置
   * v5.3 新增：用于跨区域项目迁移
   * 注意：导入后项目状态为 draft，需手动配置工具和模型后发布
   */
  static async importProject(
    data: ImportProjectRequest
  ): Promise<SuccessResponse<ImportProjectResult>> {
    return ApiClient.postWithClient<ImportProjectResult>(
      apiClients.agentAdmin,
      `/projects/import`,
      data
    )
  }
}

